import { RootState } from 'app/reducer';
import Table, { TableCol } from 'modules/Layout/component/Table';
import { PartialSearchingProps, SortParams } from 'modules/Shared/type';
import {
  setUserListParamsAction,
  SetUserListParamsAction
} from 'modules/User/action/list';
import User from 'modules/User/model/User';
import React, { createRef, ReactNode } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { ROUTE_USER_DETAILS } from 'modules/User/routes';
import ActionShow from 'modules/Layout/component/Action/Show';
import { getPathUrl } from 'modules/Shared/helper/api';
import ActionDelete from 'modules/Layout/component/Action/Delete';
import DeleteUserModal, {
  DeleteUserModal as DeleteUserModalClass
} from 'modules/User/component/Modal/Delete';
import ChangeUserRoleModal, {
  ChangeUserRoleModal as ChangeUserRoleModalType
} from 'modules/User/component/Modal/Role';
import ActionPublish from 'modules/Layout/component/Action/Publish';

export interface StateProps {
  auth: User;
  users: User[];
  sort: SortParams;
}

export interface DispatchProps {
  setParams: (payload: PartialSearchingProps) => SetUserListParamsAction;
}

export interface Props extends StateProps, DispatchProps {
  onDeleteClick?: (user: User) => void;
}

export const mapState = (state: RootState): StateProps => {
  const { user: auth } = state.auth;
  const { users, sort } = state.user.list;

  return { auth, users, sort };
};

export const mapDispatch = (dispatch: Dispatch): DispatchProps => ({
  setParams: (payload: PartialSearchingProps) =>
    dispatch(setUserListParamsAction(payload))
});

export class UserTable extends React.Component<Props> {
  readonly cols: TableCol<User>[];
  readonly deleteModalRef: React.RefObject<DeleteUserModalClass>;
  readonly changeRoleModalRef: React.RefObject<ChangeUserRoleModalType>;

  constructor(props: Props) {
    super(props);

    this.deleteModalRef = createRef();
    this.changeRoleModalRef = createRef();

    this.cols = [
      {
        property: 'id',
        label: 'User ID'
      },
      {
        property: 'email',
        label: 'Email',
        className: 'col-2',
        columnRedirectTo: (row) =>
          getPathUrl(ROUTE_USER_DETAILS, { id: row.id })
      },
      {
        property: 'username',
        label: 'Username',
        sortable: true,
        className: 'col-2',
        columnRedirectTo: (row) =>
          getPathUrl(ROUTE_USER_DETAILS, { id: row.id })
      },
      {
        property: 'status',
        label: 'Status',
        className: 'col-1',
        value: (row) => row.getStatus()
      },
      {
        property: 'age',
        label: 'Age',
        sortable: true,
        className: 'col-1'
      },
      {
        property: 'city',
        label: 'City',
        className: 'col-1'
      },
      {
        property: 'country',
        label: 'Country',
        sortable: true,
        className: 'col-1'
      },
      {
        property: 'plants_knowledge',
        label: 'Plants knowledge',
        value: (row) => row.getPlantsKnowledge(),
        className: 'col-1'
      },
      {
        property: 'plants_owned',
        label: 'Plants owned',
        value: (row) => row.getPlantsOwned(),
        className: 'col-1'
      },
      {
        property: 'created_at',
        label: 'Joined at',
        sortable: true,
        className: 'col-2'
      },
      {
        property: null,
        label: 'Action',
        value: (row) => (
          <div className="actions-wrapper">
            <ActionShow
              title="Show user"
              to={getPathUrl(ROUTE_USER_DETAILS, { id: row.id })}
            />
            <ActionPublish
              id={`change-role-${row.id}`}
              disabled={row.isDeleted()}
              title="Make administrator"
              onClick={() => this.changeRoleModalRef?.current?.open(row)}
            />
            <ActionDelete
              id={`delete-user-${row.id}`}
              disabled={row.isDeleted()}
              title="Delete user"
              onClick={() => this.deleteModalRef?.current?.open(row)}
            />
          </div>
        )
      }
    ];
  }

  render(): ReactNode {
    const { users, sort, setParams } = this.props;

    return (
      <>
        <DeleteUserModal
          ref={this.deleteModalRef}
          onSuccess={() => setParams({ sort })}
        />
        <ChangeUserRoleModal
          ref={this.changeRoleModalRef}
          /* eslint-disable-next-line jsx-a11y/aria-role */
          role="admin"
          onSuccess={() => setParams({ sort })}
        />
        <Table
          cols={this.cols}
          rows={users}
          sort={sort}
          onSort={(params) => setParams({ sort: params })}
        />
      </>
    );
  }
}

export default connect<StateProps, DispatchProps>(
  mapState,
  mapDispatch
)(UserTable);
